import React, { useMemo, useState, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { AppCheckbox, CheckboxHolder } from "./form";
import DatePicker from "react-datepicker";
import dayjs from "dayjs";
import CategorySelectGrouped from "./transactions/CategorySelectGrouped.jsx";
import { authFetch } from "../api/authFetch";

/* ================= helpers ================= */

function splitKeywords(str) {
    return (str || "")
        .split(",")
        .map((s) =>
            s
                .toString()
                .toLowerCase()
                .replace(/i/g, "і")
                .replace(/\s+/g, " ")
                .trim()
        )
        .filter(Boolean);
}

/* ================= component ================= */

export default function TxRuleModal({
                                        show,
                                        onClose,
                                        onApplied,
                                        onEditRuleUpdated,
                                        editRule = null,
                                        user,
                                        /** YYYY-MM — період для перекатегоризації після збереження (екранний місяць). */
                                        applyPeriodMonth = null,
                                    }) {
    /* ---------- form state ---------- */

    const [name, setName] = useState("");
    const [resultCategoryType, setResultCategoryType] = useState("business");
    const [resultCategory, setResultCategory] = useState("");
    const [priority, setPriority] = useState(100);
    const [conditionCategoryType, setConditionCategoryType] = useState("business");
    const [cardTypes, setCardTypes] = useState([]);


    const [descKeywords, setDescKeywords] = useState("");
    const [toKeywords, setToKeywords] = useState("");
    const [fromKeywords, setFromKeywords] = useState("");

    const [paymentType, setPaymentType] = useState("");
    const [direction, setDirection] = useState("");

    const [dateFrom, setDateFrom] = useState(() =>
        dayjs().startOf("month").toDate()
    );
    const [dateTo, setDateTo] = useState(() =>
        dayjs().endOf("month").toDate()
    );

    const [loading, setLoading] = useState(false);

    /** Під час кожного відкриття модалки заново підтягуємо категорії з БД у селекті. */
    const [categoryPickerEpoch, setCategoryPickerEpoch] = useState(0);

    const CARD_TYPE_OPTIONS = [
        { value: "fop", label: "ФОП" },
        { value: "white", label: "Біла карта" },
        { value: "black", label: "Чорна карта" },
        { value: "novapay_fop", label: "NovaPay ФОП" },
    ];

    function toggleCardType(value) {
        setCardTypes(prev =>
            prev.includes(value)
                ? prev.filter(v => v !== value)
                : [...prev, value]
        );
    }




    useEffect(() => {
        if (paymentType === "cash") {
            setCardTypes([]); // ❗ наличка → никаких карт
        }
    }, [paymentType]);

    useEffect(() => {
        if (show) setCategoryPickerEpoch((n) => n + 1);
    }, [show]);

    /** Дати перекатегоризації збігаються з обраним на сторінці місяцем (або поточним місяцем). */
    useEffect(() => {
        if (!show) return;
        if (applyPeriodMonth && /^\d{4}-\d{2}$/.test(applyPeriodMonth)) {
            setDateFrom(dayjs(applyPeriodMonth).startOf("month").toDate());
            setDateTo(dayjs(applyPeriodMonth).endOf("month").toDate());
        } else if (!editRule) {
            setDateFrom(dayjs().startOf("month").toDate());
            setDateTo(dayjs().endOf("month").toDate());
        }
    }, [show, applyPeriodMonth, editRule]);


    /* ---------- payload ---------- */

    const payload = useMemo(
        () => ({
            name: name.trim(),
            priority,
            enabled: true,
            result: {
                categoryType: resultCategoryType,
                category: resultCategory,
            },
            condition: {
                categoryTypes: conditionCategoryType ? [conditionCategoryType] : [],
                directions: direction ? [direction] : [],
                paymentTypes: paymentType ? [paymentType] : [],
                cardTypes: paymentType === "non-cash" ? cardTypes : [],
                descriptionAny: splitKeywords(descKeywords),
                toAny: splitKeywords(toKeywords),
                fromAny: splitKeywords(fromKeywords),
                mode: "any",
            },
        }),
        [
            name,
            priority,
            conditionCategoryType,
            resultCategoryType,
            resultCategory,
            direction,
            paymentType,
            cardTypes,
            descKeywords,
            toKeywords,
            fromKeywords,
        ]
    );

    /* ---------- save rule ---------- */

    async function handleSaveRule() {
        if (!name || !resultCategory) {
            alert("Назва правила та категорія обовʼязкові");
            return;
        }

        const fromDay = dayjs(dateFrom).startOf("day");
        const toDay = dayjs(dateTo).endOf("day");
        if (fromDay.isAfter(toDay)) {
            alert("Початок періоду не може бути пізніше за кінець");
            return;
        }

        const fromIso = fromDay.toISOString();
        const toIso = toDay.toISOString();

        setLoading(true);
        try {
            const res = await authFetch(
                editRule
                    ? `/api/category-rules/admin/${editRule._id}`
                    : "/api/category-rules/admin",
                {
                    method: editRule ? "PUT" : "POST",
                    headers: { "Content-Type": "application/json" },
                    credentials: "include",
                    body: JSON.stringify(payload),
                }
            );

            const json = await res.json();
            if (!json.success) {
                alert(json.message || json.error || `Помилка збереження правила (HTTP ${res.status})`);
                return;
            }

            if (editRule) {
                onEditRuleUpdated?.(json.rule);
            }

            /** Лише це правило + обраний період; транзакції з ручною категорією на бекенді пропускаються. */
            const recRes = await authFetch("/api/category-rules/apply-saved-rule-period", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({
                    ruleId: json.rule._id,
                    from: fromIso,
                    to: toIso,
                }),
            });
            const recJson = await recRes.json().catch(() => ({}));
            if (!recRes.ok) {
                alert(
                    recJson.message ||
                        "Правило збережено, але не вдалося перекатегоризувати період"
                );
            }

            onApplied?.();

            resetAndClose();

        } finally {
            setLoading(false);
        }
    }

    /* ---------- edit mode ---------- */

    useEffect(() => {
        if (!editRule) return;

        setName(editRule.name || "");
        setPriority(editRule.priority ?? 100);
        setCardTypes(editRule.condition?.cardTypes || []);
        setConditionCategoryType(
            editRule.condition?.categoryTypes?.[0] || "business"
        );


        setResultCategoryType(editRule.result.categoryType);
        setResultCategory(editRule.result.category);

        setDescKeywords(
            splitKeywords((editRule.condition?.descriptionAny || []).join(", ")).join(", ")
        );
        setToKeywords(
            splitKeywords((editRule.condition?.toAny || []).join(", ")).join(", ")
        );
        setFromKeywords(
            splitKeywords((editRule.condition?.fromAny || []).join(", ")).join(", ")
        );

        setDirection(editRule.condition?.directions?.[0] || "");
        setPaymentType(editRule.condition?.paymentTypes?.[0] || "");
    }, [editRule]);

    /* ---------- reset ---------- */

    function resetAndClose() {
        setName("");
        setConditionCategoryType("business");
        setResultCategoryType("business");
        setResultCategory("");
        setDescKeywords("");
        setToKeywords("");
        setCardTypes([]);
        setFromKeywords("");
        setPaymentType("");
        setDirection("");
        setPriority(100);
        onClose();
    }

    /* ---------- render ---------- */

    return (
        <Modal show={show} onHide={resetAndClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Правило категоризації</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <Form.Group className="mb-3">
                    <Form.Label>Назва правила</Form.Label>
                    <Form.Control
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Напр: Facebook → Реклама (Business)"
                    />
                </Form.Group>

                <div className="d-flex gap-3">
                    {/* -------- conditions -------- */}
                    <div style={{ flex: 1 }}>
                        <h6>Умови</h6>

                        <Form.Group className="mb-2">
                            <Form.Label>description містить</Form.Label>
                            <Form.Control
                                value={descKeywords}
                                onChange={(e) =>
                                    setDescKeywords(e.target.value)
                                }
                                onBlur={() =>
                                    setDescKeywords((v) =>
                                        splitKeywords(v).join(", ")
                                    )
                                }
                            />
                            <Form.Text className="text-muted small">
                                Ключові слова через кому; регістр неважливий — зберігаються та порівнюються в нижньому
                                регістрі (як текст опису в транзакції).
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-2">
                            <Form.Label>to містить</Form.Label>
                            <Form.Control
                                value={toKeywords}
                                onChange={(e) => setToKeywords(e.target.value)}
                                onBlur={() =>
                                    setToKeywords((v) =>
                                        splitKeywords(v).join(", ")
                                    )
                                }
                            />
                        </Form.Group>

                        <Form.Group className="mb-2">
                            <Form.Label>from містить</Form.Label>
                            <Form.Control
                                value={fromKeywords}
                                onChange={(e) =>
                                    setFromKeywords(e.target.value)
                                }
                                onBlur={() =>
                                    setFromKeywords((v) =>
                                        splitKeywords(v).join(", ")
                                    )
                                }
                            />
                        </Form.Group>
                        <Form.Group className="mb-2">
                            <Form.Label>Застосовується до</Form.Label>
                            <Form.Select
                                value={conditionCategoryType}
                                onChange={(e) => setConditionCategoryType(e.target.value)}
                            >
                                <option value="business">Бізнес</option>
                                {user.role === "admin" && (
                                    <option value="personal">Особисті</option>
                                )}
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className="mb-2">
                            <Form.Label>Тип оплати</Form.Label>
                            <Form.Select
                                value={paymentType}
                                onChange={(e) =>
                                    setPaymentType(e.target.value)
                                }
                            >
                                <option value="">Будь-який</option>
                                <option value="non-cash">Безготівка</option>
                                <option value="cash">Готівка</option>
                            </Form.Select>
                        </Form.Group>
                        {paymentType === "non-cash" && (
                            <Form.Group className="mb-2">
                                <Form.Label>Карти (безготівка)</Form.Label>

                                <CheckboxHolder className="mb-0">
                                    {CARD_TYPE_OPTIONS.map((opt) => (
                                        <AppCheckbox
                                            key={opt.value}
                                            id={`card-${opt.value}`}
                                            label={opt.label}
                                            checked={cardTypes.includes(opt.value)}
                                            onChange={() => toggleCardType(opt.value)}
                                        />
                                    ))}
                                </CheckboxHolder>
                            </Form.Group>
                        )}
                        <Form.Group className="mb-2">
                            <Form.Label>Direction</Form.Label>
                            <Form.Select
                                value={direction}
                                onChange={(e) => setDirection(e.target.value)}
                            >
                                <option value="">Будь-який</option>
                                <option value="expense">expense</option>
                                <option value="income">income</option>
                            </Form.Select>
                        </Form.Group>
                    </div>

                    {/* -------- result -------- */}
                    <div style={{ flex: 1 }}>
                        <h6>Результат</h6>

                        <Form.Group className="mb-2">
                            <Form.Label>CategoryType</Form.Label>
                            <Form.Select
                                value={resultCategoryType}
                                onChange={(e) =>
                                    setResultCategoryType(e.target.value)
                                }
                            >
                                <option value="business">Business</option>
                                {user.role === "admin" && (
                                    <option value="personal">Personal</option>
                                )}
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-2">
                            <Form.Label>Категорія</Form.Label>
                            <CategorySelectGrouped
                                key={`tx-rule-cat-${categoryPickerEpoch}-${resultCategoryType}`}
                                apiType={resultCategoryType}
                                value={resultCategory}
                                onChange={setResultCategory}
                                className="form-select"
                                fetchPolicy="alwaysFresh"
                                fallbackToConstants={false}
                            />
                        </Form.Group>

                        <Form.Group className="mb-2">
                            <Form.Label>Пріоритет</Form.Label>
                            <Form.Control
                                type="number"
                                value={priority}
                                onChange={(e) =>
                                    setPriority(+e.target.value)
                                }
                            />
                        </Form.Group>

                        <h6 className="mb-1">Період застосування цього правила</h6>
                        <Form.Text className="text-muted small d-block mb-2">
                            Після збереження перевіряється лише це правило. Транзакції не змінюються, якщо ви вручну змінили
                            тип або категорію, або зберігали власний коментар до операції. Повний перезапуск усіх правил —
                            кнопка «Перекатегоризувати період» на дошці.
                        </Form.Text>
                        <div className="d-flex gap-2 flex-wrap">
                            <DatePicker
                                selected={dateFrom}
                                onChange={setDateFrom}
                                className="form-control"
                                dateFormat="yyyy-MM-dd"
                            />
                            <DatePicker
                                selected={dateTo}
                                onChange={setDateTo}
                                className="form-control"
                                dateFormat="yyyy-MM-dd"
                            />
                        </div>
                    </div>
                </div>
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={resetAndClose}>
                    Закрити
                </Button>

                <Button
                    variant="outline-primary"
                    onClick={handleSaveRule}
                    disabled={loading}
                >
                    💾 Зберегти правило
                </Button>
            </Modal.Footer>
        </Modal>
    );
}